/**
 * Règles de navigation, sans dépendance au routeur : testables isolément.
 *
 * - `guest` : écrans de connexion. Un utilisateur connecté est renvoyé vers l'application.
 * - `member` : écrans de l'application. Un visiteur est renvoyé vers la connexion, avec retour
 *   prévu vers la page demandée. Tant que l'onboarding n'est pas terminé, on y est conduit.
 * - `onboarding` : réservé à un utilisateur connecté qui n'a pas terminé la configuration.
 * - `admin` : réservé aux administrateurs ; les autres sont renvoyés à l'accueil.
 * - `public` : accessible à tous (retour d'e-mail, page introuvable).
 */

export type Access = 'guest' | 'member' | 'onboarding' | 'admin' | 'public'

export interface SessionFacts {
  signedIn: boolean
  onboardingCompleted: boolean
  isAdmin: boolean
  /** Session ouverte par un lien de réinitialisation : seul l'écran de nouveau mot de passe est permis. */
  recovering: boolean
}

export type Destination =
  { kind: 'allow' } | { kind: 'redirect'; name: string; query?: Record<string, string> }

export const HOME = 'home'
export const SIGN_IN = 'sign-in'
export const ONBOARDING = 'onboarding'
export const NEW_PASSWORD = 'new-password'

/** N'accepte qu'un chemin interne : empêche toute redirection ouverte vers un autre site. */
export function safeRedirect(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return null
  return value
}

export function resolveDestination(
  target: { access: Access; name: string; fullPath: string },
  session: SessionFacts,
): Destination {
  if (session.recovering && target.name !== NEW_PASSWORD) {
    return { kind: 'redirect', name: NEW_PASSWORD }
  }

  switch (target.access) {
    case 'public':
      return { kind: 'allow' }
    case 'guest':
      if (!session.signedIn || target.name === NEW_PASSWORD) return { kind: 'allow' }
      return { kind: 'redirect', name: session.onboardingCompleted ? HOME : ONBOARDING }
    case 'onboarding':
      if (!session.signedIn) return { kind: 'redirect', name: SIGN_IN }
      return session.onboardingCompleted ? { kind: 'redirect', name: HOME } : { kind: 'allow' }
    case 'admin':
      if (!session.signedIn) return { kind: 'redirect', name: SIGN_IN }
      return session.isAdmin ? { kind: 'allow' } : { kind: 'redirect', name: HOME }
    case 'member':
      if (!session.signedIn) {
        return { kind: 'redirect', name: SIGN_IN, query: { suite: target.fullPath } }
      }
      return session.onboardingCompleted
        ? { kind: 'allow' }
        : { kind: 'redirect', name: ONBOARDING }
  }
}
